import NotificationBanner from "@/components/Common/Banner/NotificationBanner";
import ContentWrapper from "@/components/Common/Containers/ContentContainer";
import DashboardSkeleton from "@/components/Dashboard/DashboardSkeleton";
import BeneficiaryCard from "@/components/Dashboard/BeneficiaryCard";
import ErrorDisplay from "@/components/Common/ErrorDisplay";
import VestingModal from "@/components/Common/Modals/VestingModal";
import { RootState } from "@/store";
import { formatMoney } from "@/utils/generalUtilities";
import { getTenantFromHash, lightenHex } from "@/utils/getBrandings";
import { useBeneficiariesData } from "@/hooks/useBeneficiariesData";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LinkIcon from "@assets/external-link.png";

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const [showVestingModal, setShowVestingModal] = useState(false);
  const {
    beneficiaries,
    totals,
    isLoading,
    error,
    showBanner,
    bannerMessage,
    setShowBanner,
  } = useBeneficiariesData(user.employeeID, user.id, user.enrolledStatus);

  const handleManageContributions = useCallback(
    (accountNumber: string) => {
      navigate(`/manage-account/${accountNumber}`);
    },
    [navigate],
  );
  const partner = getTenantFromHash();
  const gradientFrom = partner ? window.partner.colors.primary : "#58D4D4";
  const gradientTo = partner
    ? lightenHex(window.partner.colors.primary, 0.85)
    : "#ADF7F7";

  const handleDismissBanner = () => {
    setShowBanner(false);
  };

  return (
    <>
      <VestingModal
        isOpen={showVestingModal}
        onClose={() => setShowVestingModal(false)}
      />

      <ContentWrapper>
        {isLoading ? (
          <div className="bg-white h-[80vh]">
            <DashboardSkeleton />
          </div>
        ) : error ? (
          <ErrorDisplay error={error} />
        ) : (
          <div className="relative h-full bg-white">
            <div className="hidden lg:block absolute left-0 top-0 h-[608px] w-[10%] bg-[url('/gradient-bg-left.svg')]  bg-no-repeat bg-contain"></div>
            <div className="hidden lg:block absolute top-0 right-0 h-[608px] w-[10%] bg-[url('/gradient-bg-right.svg')]  bg-no-repeat bg-contain "></div>

            <div className="w-full lg:w-[830px] mx-auto font-outfit  pt-[15px] md:pt-[48px] pb-[156px]">
              <div className="flex items-center justify-between">
                <h1 className="text-[20px] md:text-[32px] px-[16px] lg:px-0 font-semibold">
                  {t("home.account_overview")}
                </h1>

                <div
                  onClick={() => setShowVestingModal(true)}
                  className="cursor-pointer flex text-[#00ADAC] items-center space-x-1 px-[16px] lg:px-0"
                >
                  <p>{t("home.learn_more_vesting")}</p>
                  <img
                    src={LinkIcon}
                    alt={t("home.alt_text.external_link")}
                    className="w-[18px] h-[18px]"
                  />
                </div>
              </div>

              <div className="my-6">
                <NotificationBanner
                  message={bannerMessage}
                  onDismiss={handleDismissBanner}
                  isVisible={showBanner}
                  showDismissButton
                />
              </div>
              <div id="mobile-hero" className="md:hidden pt-[15px]">
                <div
                  className={`relative bg-gradient-to-b from-[#58D4D4"] to-[#ADF7F7]  lg:col-span-2`}
                >
                  <div className="px-[16px] py-[32px]   rounded-r-[24px]">
                    <h2 className="text-[18px] mb-6">
                      {t("home.paycheck_summary.each_paycheck")}
                    </h2>
                    <div className="flex items-center mb-8">
                      <div className="mr-7">
                        <p className="mb-1">
                          {t("home.paycheck_summary.you_contribute")}
                        </p>
                        <p className="text-[28px] font-bold">
                          $ {formatMoney(totals.employeeContributions)}
                        </p>
                      </div>
                      <div>
                        <p className="mb-1">
                          {t("home.paycheck_summary.employer_adds")}
                        </p>
                        <p className="text-[28px] font-bold">
                          + $ {formatMoney(totals.employerContributions)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 border-b border-[#E2E2E2]">
                  <div className="px-[16px] py-[32px] border-r-[1px] border-[#E2E2E2]">
                    <p>{t("home.beneficiary_card.total_savings")}</p>
                    <p className="text-[24px] lg:text-[30px] font-semibold mt-3">
                      $ {formatMoney(Math.trunc(totals.totalSavings))}
                    </p>
                  </div>
                  <div className="px-[16px] py-[32px]">
                    <p>{t("home.beneficiary_card.employer")} </p>
                    <p className="text-[24px] lg:text-[30px] font-semibold mt-3">
                      ${" "}
                      {formatMoney(
                        Math.trunc(totals.totalEmployerContributions),
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div
                id="desktophero"
                className="hidden md:grid grid-cols-1 md:grid-cols-3 my-[40px] mx-[16px] lg:mx-0 rounded-[24px] border border-[#E2E2E2]"
              >
                <div className="flex flex-col justify-between">
                  <div className="rounded-tl-[24px] p-[32px]">
                    <p>{t("home.beneficiary_card.total_savings")}</p>
                    <p className="text-[30px] font-semibold mt-3">
                      $ {formatMoney(Math.trunc(totals.totalSavings))}
                    </p>
                  </div>
                  <div className="border-[0.5px] border-[#E2E2E2]"></div>
                  <div className=" rounded-bl-[24px] p-[32px]">
                    <p>{t("home.beneficiary_card.employer_contributions")}</p>
                    <p className="text-[30px] font-semibold mt-3">
                      ${" "}
                      {formatMoney(
                        Math.trunc(totals.totalEmployerContributions),
                      )}
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    background: `linear-gradient(to bottom right, ${gradientFrom}, ${gradientTo})`,
                  }}
                  className={`relative rounded-r-[24px] bg-gradient-to-br from-[${gradientFrom}] to-[${gradientTo}]  md:col-span-2 `}
                >
                  <div className="p-[32px] absolute inset-0  rounded-r-[24px] bg-[url('/dolphinAccounts.png')]  bg-no-repeat bg-[length:contain] bg-[position:bottom_-20px_right_0px]">
                    <h2 className="text-[18px] mb-6">
                      {t("home.paycheck_summary.each_paycheck")}
                    </h2>
                    <div className="flex items-center mb-4">
                      <div className="mr-7">
                        <p className="mb-1">
                          {t("home.paycheck_summary.you_contribute")}
                        </p>
                        <p className="text-[28px] font-bold">
                          ${" "}
                          {formatMoney(
                            Math.trunc(totals.employeeContributions),
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="mb-1">
                          {t("home.paycheck_summary.employer_adds")}
                        </p>
                        <p className="text-[28px] font-bold">
                          + ${" "}
                          {formatMoney(
                            Math.trunc(totals.employerContributions),
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {beneficiaries.map((beneficiary, index) => (
                <BeneficiaryCard
                  key={beneficiary.accountNumber || index}
                  beneficiary={beneficiary}
                  onManageContributions={handleManageContributions}
                />
              ))}
            </div>
          </div>
        )}
      </ContentWrapper>
    </>
  );
};

export default Home;
